import { useEffect, useCallback, useMemo, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { DESKTOP, PHONE } from '_utils/constant'
import {
  getCreateEventData,
  getUserInfo,
  removeCreateEventData,
  getDeviceUser,
  setDeviceUser,
} from '_utils/localData'
import { getListPackingList } from '_redux/modules/event'
import { DeviceUserContext } from '_context/DeviceUserContext'
import useBlockFeatureCheck from '_hooks/useBlockFeatureCheck'

const useFooter = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { myEvents, listPackingList } = useSelector((state) => state.event)
  const { setCheckDeviceUser } = useContext(DeviceUserContext)
  const { checkAndProceed, BlockPopup } = useBlockFeatureCheck()

  const currentUser = getUserInfo()
  const isAllowHost = useMemo(() => Boolean(
      currentUser?.groups?.length && currentUser?.groups?.some((g) => g?.isHost || g?.isAdmin)
    ), [currentUser])

  useEffect(() => {
    if (isAllowHost) {
      dispatch(getListPackingList)
    }
  }, [dispatch, isAllowHost])

  const pathSegments = useMemo(() => (location.pathname || '').split('/').filter(Boolean), [location.pathname])

  const isNewEventActive = useMemo(() => {
    const createPaths = [
      'create',
      'create-items',
      'created-confirm',
      'update',
      'update-items',
      'update-confirm',
      'hosting',
    ]
    return pathSegments[0] === 'event' && createPaths.includes((pathSegments[1] || '').split('?')[0])
  }, [pathSegments])

  const isHostedEventActive = useMemo(
    () =>
      pathSegments[0] === 'event' &&
      ['list-my-events', 'list-hosted-by-group'].includes((pathSegments[1] || '').split('?')[0]),
    [pathSegments]
  )

  const isPackagesActive = useMemo(
    () =>
      pathSegments[0] === 'event' &&
      ['packing-list', 'list-packing-list'].includes((pathSegments[1] || '').split('?')[0]),
    [pathSegments]
  )

  const hasSwitchView = useMemo(() => {
    if (!currentUser?.isUsingFeature) return false
    const isDesktop = getDeviceUser() === DESKTOP
    if (isDesktop) return true
    return typeof window !== 'undefined' && window.screen?.width >= 1203
  }, [currentUser])

  const handleNewEvent = useCallback(() => {
    checkAndProceed(() => {
      const eventData = getCreateEventData()
      if (eventData?.id) {
        removeCreateEventData()
      }
      history.push('/event/hosting')
    })
  }, [checkAndProceed, history])

  const handleHostedEvent = useCallback(() => {
    const isDesktop = getDeviceUser() === DESKTOP
    if (isDesktop) {
      history.push('/event/list-hosted-by-group')
    } else {
      history.push('/event/list-my-events')
    }
  }, [history])

  const handlePackages = useCallback(() => {
    history.push('/event/list-packing-list')
  }, [history])

  const handleSwitchView = useCallback(() => {
    const isDesktop = getDeviceUser() === DESKTOP
    if (isDesktop) {
      setDeviceUser(PHONE)
      setCheckDeviceUser?.(PHONE)
      history.push('/home')
    } else {
      setDeviceUser(DESKTOP)
      setCheckDeviceUser?.(DESKTOP)
      history.push('/manage-orders')
    }
  }, [history, setCheckDeviceUser])

  return {
    isAllowHost,
    isNewEventActive,
    isHostedEventActive,
    isPackagesActive,
    hasSwitchView,
    handleNewEvent,
    handleHostedEvent,
    handlePackages,
    handleSwitchView,
    myEventsCount: myEvents?.length || 0,
    packagesCount: listPackingList?.length || 0,
    hasPackages: currentUser?.isUsingFeature && Boolean(listPackingList?.length),
    BlockPopup,
  }
}

export default useFooter
